import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone, Heart, Star, ChevronDown, Award, Calendar, Flower2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useRef } from "react";
import { useCountUp } from "@/hooks/useCountUp";
import { FloatingPetals } from "@/components/marketing/FloatingPetals";
import heroImage from "@/assets/gallery/weddings/ceremony-setup-01.jpg";

const stats = [
  { icon: Star, value: 40, suffix: "+", label: "Years Experience" },
  { icon: Heart, value: 500, suffix: "+", label: "Weddings Designed" },
  { icon: Award, value: null, display: "Disney", label: "Trained Florist" },
  { icon: Calendar, value: null, display: "Custom", label: "Consultations" },
];

const eventTags = ["Weddings", "Ceremonies", "Receptions", "Corporate Events"];

function AnimatedStat({ stat }: { stat: typeof stats[number] }) {
  const { count, ref } = useCountUp(stat.value ?? 0, 2000);
  return (
    <span ref={ref} className="text-white font-bold text-xl leading-none">
      {stat.value !== null ? `${count}${stat.suffix || ""}` : stat.display}
    </span>
  );
}

export function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center overflow-hidden pt-32 lg:pt-40"
      style={{ background: "linear-gradient(180deg, #2A2220 0%, #1A1412 100%)" }}
    >
      {/* Parallax background */}
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={heroImage}
          alt="Elegant wedding ceremony floral arch with roses and greenery"
          fetchPriority="high"
          decoding="async"
          width={1920}
          height={1080}
          className="w-full h-full object-cover opacity-90 saturate-[1.15]"
        />
      </motion.div>

      {/* Floating petals */}
      <FloatingPetals />

      {/* Soft overlay for text readability without killing vibrancy */}
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(20,14,12,0.50) 0%, rgba(20,14,12,0.25) 60%, transparent 100%)" }} />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl text-center mx-auto">
          {/* Top badge */}
          <div className="anim-fade-up inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 dark:bg-white/5 border border-white/15 dark:border-brand-border text-white/90 mb-8 backdrop-blur-sm">
            <Flower2 size={16} className="text-primary" />
            <span className="text-sm font-medium tracking-wide">Wedding & Event Floral Designer · Cass City, MI</span>
          </div>

          {/* Headline */}
          <h1 className="anim-fade-up text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.08] mb-4 font-display drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]" style={{ animationDelay: "0.1s" }}>
            Where Love{" "}
            <br className="hidden sm:block" />
            <span className="shimmer-gradient">Blooms Beautiful</span>
          </h1>

          {/* Gold divider line */}
          <div
            className="anim-fade-in h-0.5 w-16 bg-primary rounded-full mb-6 mx-auto"
            style={{ animationDelay: "0.2s" }}
          />

          {/* Event tags */}
          <div
            className="anim-fade-up flex flex-wrap gap-2 mb-6 justify-center"
            style={{ animationDelay: "0.2s" }}
          >
            {eventTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium bg-white/10 dark:bg-brand-surface border border-white/20 dark:border-brand-border text-white/90 backdrop-blur-sm"
              >
                <Flower2 size={12} className="text-primary opacity-70" />
                {tag}
              </span>
            ))}
          </div>

          {/* Subheadline */}
          <p
            className="anim-fade-up text-lg md:text-xl text-white/90 max-w-2xl mb-10 leading-relaxed lg:mx-0 mx-auto drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)]"
            style={{ animationDelay: "0.25s" }}
          >
            Your wedding deserves flowers as unforgettable as the moment itself.
            Designed with 40 years of artistry, care, and a touch of world-class magic.
          </p>

          {/* CTAs */}
          <div
            className="anim-fade-up flex flex-col sm:flex-row items-center justify-center gap-4 mb-4"
            style={{ animationDelay: "0.3s" }}
          >
            <a href="tel:+14056273557">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-white px-8 h-14 text-lg glow-primary group btn-shine">
                <Phone size={20} className="mr-2 group-hover:rotate-12 transition-transform" />
                Call Kim — (405) 627-3557
              </Button>
            </a>
            <Link to="/floral-questionnaire">
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:border-primary/60 hover:shadow-[0_0_12px_hsl(0_50%_70%/0.2)] px-8 h-14 text-lg transition-all duration-300 group"
              >
                Request a Custom Proposal
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>

          <p
            className="anim-fade-in text-white/85 text-sm md:text-base mb-3 text-center italic"
            style={{ animationDelay: "0.32s" }}
          >
            Prefer to talk? <span className="text-primary not-italic font-semibold">Kim personally answers</span> — no call centers, no bots.
          </p>

          <p
            className="anim-fade-in text-white/60 text-sm mb-12 text-center font-medium tracking-wide"
            style={{ animationDelay: "0.35s" }}
          >
            Serving Cass City & The Thumb | 40+ Years Experience | Free Consultation
          </p>
        </div>

        {/* Trust stats bar */}
        <div
          className="anim-fade-up grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-4xl mx-auto"
          style={{ animationDelay: "0.4s" }}
        >
          {stats.map((stat, index) => (
            <div
              key={stat.label}
              className="anim-fade-up flex flex-col items-center gap-2 px-4 py-4 rounded-xl border border-t-2 border-t-primary backdrop-blur-md shadow-lg transition-all duration-300 hover:-translate-y-[3px]"
              style={{ animationDelay: `${0.5 + index * 0.08}s`, background: "rgba(44,33,30,0.6)", borderColor: "rgba(255,220,200,0.08)" }}
            >
              <stat.icon size={20} className="text-primary" />
              <AnimatedStat stat={stat} />
              <span className="text-white/70 text-xs font-medium tracking-wide">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 anim-fade-in"
        style={{ animationDelay: "1.2s" }}
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center gap-1 text-brand-text-muted"
        >
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <ChevronDown size={20} />
        </motion.div>
      </div>
    </section>
  );
}
